import { useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import throttle from "lodash-es/throttle";
import { AppDispatch, RootState } from "app/store";
import { MapModel } from "common/model";
import { appendEditHistory } from "features/builder/api";
import { CommandManager } from "features/builder/engine";
import { PieceListContext } from "features/builder/pieceList";
import { actions, mapModelAdapter } from "features/builder/slice";
import {
  ActionRecord,
  ActionCommand,
  PieceMilestone,
  ActionCommandType,
} from "features/builder/types";

// 一覧トップの縦スクロール位置を redux に保存／復元するフック。
// ready（pieces ロード完了）後にスクロール要素へ復元し、以後は throttle で保存する。
// callback ref で attach 時に復元するため、くわしく往復・タブ往復の再マウントでも効く。
export const usePieceListScroll = (context: PieceListContext, ready: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  const scrollTop = useSelector((state: RootState) => state.builder.listUi[context].scrollTop);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const savedTopRef = useRef(scrollTop);
  const readyRef = useRef(ready);
  const restoredRef = useRef(false);

  useEffect(() => {
    savedTopRef.current = scrollTop;
  }, [scrollTop]);
  useEffect(() => {
    readyRef.current = ready;
  }, [ready]);

  // ノードが既に attach 済みで ready になった瞬間に復元（初回ロード完了時）
  useEffect(() => {
    if (ready && !restoredRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = savedTopRef.current;
      restoredRef.current = true;
    }
  }, [ready]);

  // attach 時の復元（再マウント対応）。ref は安定（依存なし、ref 経由で最新値参照）。
  const ref = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    if (node && readyRef.current) {
      node.scrollTop = savedTopRef.current;
      restoredRef.current = true;
    }
  }, []);

  const saveRef = useRef(
    throttle((top: number) => dispatch(actions.setListScroll({ context, scrollTop: top })), 200, {
      leading: false,
      trailing: true,
    }),
  );

  const onScroll = useCallback(() => {
    const node = scrollRef.current;
    // 復元前の初期 0 で保存値を上書きしない
    if (!node || !restoredRef.current) return;
    saveRef.current(node.scrollTop);
  }, []);

  return { ref, onScroll, scrollRef };
};

/** キーの並びに左右されない文字列（サーバーの JSON 列はキーの順番を並べ替えて返す） */
const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
};

/** マップと大きさが同じか（置いた順番には左右されない） */
const sameState = (aMap: MapModel[], aSize: unknown, bMap: MapModel[], bSize: unknown) => {
  const key = (models: MapModel[]) =>
    stableJson([...models].sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0)));
  return key(aMap) === key(bMap) && stableJson(aSize) === stableJson(bSize);
};

/**
 * 記録に 1 件足す（Redux にすぐ反映し、サーバーへ送る）。そのときのマップを restore 用に残す。
 * 操作・undo / redo・エディタを開いた／閉じた・restore のどれもここを通す（docs/edit-history-undo.md）
 */
export const recordEditHistory =
  (action_type: string, payload?: Record<string, unknown>) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { builder, user } = getState();
    const map = mapModelAdapter.getSelectors().selectAll(builder.map);
    const size = builder.size;
    const last = builder.editHistory.at(-1);
    // 開いた・閉じたは、前の記録から中身が変わっていなければ積まない（開き直すたびに区切りが増えないように。
    // じゆうにつくるも同じ中身なら積まない）
    const isOpenOrClose =
      action_type === PieceMilestone.OPEN || action_type === PieceMilestone.CLOSE;
    if (isOpenOrClose && last?.map && sameState(last.map, last.size, map, size)) return;
    dispatch(
      actions.pushEditHistory({
        seq: (last?.seq ?? 0) + 1,
        version: last?.version ?? 1,
        action_type,
        payload: payload as Record<string, string | number | boolean> | undefined,
        map,
        size,
        created_at: new Date().toISOString(),
      }),
    );
    const uid = user.active?.uid;
    const pid = builder.editingBuilderMapId;
    if (uid && pid && pid !== "local-map") {
      appendEditHistory({
        piece_id: pid,
        uid,
        actions: [{ action_type, payload, map, size }],
      }).catch(() => undefined);
    }
  };

/** undo / redo の対象を、記録の操作の種類に直す（「もとにもどした：〇〇」の〇〇） */
const undoneOf = (record: ActionRecord): Record<string, unknown> => {
  switch (record.type) {
    case ActionCommandType.ADD_MODE:
      return { undone: "place_asset", name: record.forward.model?.name };
    case ActionCommandType.REMOVE_MODE:
      return { undone: "remove_asset", name: record.backward.model?.name };
    case ActionCommandType.CHANGE_MODE:
      return { undone: "change_asset" };
    case ActionCommandType.REPLACE_ALL:
      return { undone: record.label ?? "ai_generate" };
    default:
      return {};
  }
};

export const useActionCommand = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const commandManager = useRef(new CommandManager());

  // 記録に残す（中身は recordEditHistory）
  const recordHistory = useCallback(
    (action_type: string, payload?: Record<string, unknown>) =>
      dispatch(recordEditHistory(action_type, payload)),
    [dispatch],
  );

  // アクション実行（履歴記録付き）
  const execute = useCallback(
    (command: ActionCommand) => {
      switch (command.type) {
        case ActionCommandType.ADD_MODE: {
          dispatch(actions.addModelToMap(command.model));
          // Undo用に記録
          const record = commandManager.current.recordAdd(command.model);
          dispatch(actions.pushUndo(record));
          dispatch(actions.clearRedo());
          recordHistory("place_asset", { name: command.model.name });
          break;
        }
        case ActionCommandType.REMOVE_MODE: {
          // 削除前のモデルを取得してUndoに記録
          // Note: このフックではstoreにアクセスできないので、
          // モデル情報はコンポーネント側で渡す必要がある
          dispatch(actions.removeModelFromMap(command.meshId));
          recordHistory("remove_asset");
          break;
        }
        case ActionCommandType.CHANGE_MODE: {
          dispatch(
            actions.updateMapModel({
              id: command.id,
              changes: command.changes,
            }),
          );
          recordHistory("change_asset", { id: command.id });
          break;
        }
        default:
          break;
      }
    },
    [dispatch, recordHistory],
  );

  // Undo実行（共有された Redux スタックの末尾レコードを使う）
  const undo = useCallback(() => {
    const record = store.getState().builder.undoStacks.at(-1);
    if (!record) return null;

    // backward操作を実行
    switch (record.type) {
      case ActionCommandType.ADD_MODE:
        // ADDの取り消し = 削除
        if (record.backward.meshId) {
          dispatch(actions.removeModelFromMap(record.backward.meshId));
        }
        break;
      case ActionCommandType.REMOVE_MODE:
        // REMOVEの取り消し = 追加
        if (record.backward.model) {
          dispatch(actions.addModelToMap(record.backward.model));
        }
        break;
      case ActionCommandType.REPLACE_ALL:
        // マップ差し替えの取り消し = 差し替え前のマップに戻す（restore は大きさも）
        if (record.backward.models) {
          dispatch(
            actions.replaceMapState({ models: record.backward.models, size: record.backward.size }),
          );
        }
        break;
      case ActionCommandType.CHANGE_MODE:
        // CHANGEの取り消し = 前の値に戻す
        if (record.backward.id && record.backward.changes) {
          dispatch(
            actions.updateMapModel({
              id: record.backward.id,
              changes: record.backward.changes,
            }),
          );
        }
        break;
    }

    dispatch(actions.popUndo());
    dispatch(actions.pushRedo(record));
    // undo も記録に残す（記録の最新がいつも画面と一致するように）
    recordHistory("undo", undoneOf(record));

    return record;
  }, [dispatch, store, recordHistory]);

  // Redo実行（共有された Redux スタックの末尾レコードを使う）
  const redo = useCallback(() => {
    const record = store.getState().builder.redoStacks.at(-1);
    if (!record) return null;

    // forward操作を再実行
    switch (record.type) {
      case ActionCommandType.ADD_MODE:
        if (record.forward.model) {
          dispatch(actions.addModelToMap(record.forward.model));
        }
        break;
      case ActionCommandType.REMOVE_MODE:
        if (record.forward.meshId) {
          dispatch(actions.removeModelFromMap(record.forward.meshId));
        }
        break;
      case ActionCommandType.REPLACE_ALL:
        if (record.forward.models) {
          dispatch(
            actions.replaceMapState({ models: record.forward.models, size: record.forward.size }),
          );
        }
        break;
      case ActionCommandType.CHANGE_MODE:
        if (record.forward.id && record.forward.changes) {
          dispatch(
            actions.updateMapModel({
              id: record.forward.id,
              changes: record.forward.changes,
            }),
          );
        }
        break;
    }

    dispatch(actions.popRedo());
    dispatch(actions.pushUndo(record));
    recordHistory("redo", undoneOf(record));

    return record;
  }, [dispatch, store, recordHistory]);

  // 削除時に履歴記録（モデル情報付き）
  const executeRemove = useCallback(
    (model: MapModel) => {
      dispatch(actions.removeModelFromMap(model.id));
      const record = commandManager.current.recordRemove(model);
      dispatch(actions.pushUndo(record));
      dispatch(actions.clearRedo());
      recordHistory("remove_asset", { name: model.name });
    },
    [dispatch, recordHistory],
  );

  // 変更時に履歴記録（前後の値付き）
  const executeChange = useCallback(
    (
      id: string,
      newChanges: { [key: string]: unknown },
      oldChanges: { [key: string]: unknown },
    ) => {
      dispatch(
        actions.updateMapModel({
          id,
          changes: newChanges as { [key: string]: string },
        }),
      );
      const record = commandManager.current.recordChange(
        id,
        newChanges as { [key: string]: string },
        oldChanges as { [key: string]: string },
      );
      dispatch(actions.pushUndo(record));
      dispatch(actions.clearRedo());
      recordHistory("change_asset", { id });
    },
    [dispatch, recordHistory],
  );

  // Undo/Redo可能かチェック（共有された Redux スタックを参照）
  const canUndo = useCallback(() => store.getState().builder.undoStacks.length > 0, [store]);
  const canRedo = useCallback(() => store.getState().builder.redoStacks.length > 0, [store]);

  // 履歴クリア
  const clearHistory = useCallback(() => {
    commandManager.current.clear();
    dispatch(actions.clearHistory());
  }, [dispatch]);

  // 後方互換性：既存コードで handleAction(command) として使用可能
  const handleAction = execute;

  return {
    execute,
    handleAction, // 後方互換性のエイリアス
    executeRemove,
    executeChange,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
};
